/**
 * RecurrencePicker Component
 * 
 * A reusable component for configuring recurring patterns
 * for tasks, action items, and scheduled events.
 */

import React, { useState, useEffect } from 'react'
import { Repeat, Calendar, Clock, ChevronDown, ChevronUp, Info } from 'lucide-react'

export interface RecurrenceRule {
  frequency: 'daily' | 'weekdays' | 'weekly' | 'biweekly' | 'monthly' | 'custom'
  interval?: number
  days_of_week?: number[] // 0 = Sunday, 1 = Monday, etc.
  day_of_month?: number
  time?: string
  end_date?: string
  occurrences?: number
}

interface RecurrencePickerProps {
  value?: RecurrenceRule | null
  onChange: (rule: RecurrenceRule | null) => void
  disabled?: boolean
  className?: string
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', short: 'S' },
  { value: 1, label: 'Mon', short: 'M' },
  { value: 2, label: 'Tue', short: 'T' },
  { value: 3, label: 'Wed', short: 'W' },
  { value: 4, label: 'Thu', short: 'T' },
  { value: 5, label: 'Fri', short: 'F' },
  { value: 6, label: 'Sat', short: 'S' },
]

const PRESETS: { value: RecurrenceRule['frequency']; label: string; description: string }[] = [
  { value: 'daily', label: 'Daily', description: 'Every day' },
  { value: 'weekdays', label: 'Weekdays', description: 'Monday to Friday' },
  { value: 'weekly', label: 'Weekly', description: 'Same day each week' },
  { value: 'biweekly', label: 'Bi-weekly', description: 'Every two weeks' },
  { value: 'monthly', label: 'Monthly', description: 'Same day each month' },
  { value: 'custom', label: 'Custom', description: 'Create your own pattern' },
]

export const RecurrencePicker: React.FC<RecurrencePickerProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  const [isEnabled, setIsEnabled] = useState(!!value)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [rule, setRule] = useState<RecurrenceRule>(value || {
    frequency: 'weekly',
    interval: 1,
    days_of_week: [new Date().getDay()], // Default to today
    time: '09:00'
  })

  // Sync with external value changes
  useEffect(() => {
    if (value) {
      setRule(value)
      setIsEnabled(true)
    } else {
      setIsEnabled(false)
    }
  }, [value])

  const handleEnableToggle = () => {
    if (isEnabled) {
      setIsEnabled(false)
      onChange(null)
    } else {
      setIsEnabled(true)
      onChange(rule)
    }
  }

  const updateRule = (updates: Partial<RecurrenceRule>) => {
    const newRule = { ...rule, ...updates }
    setRule(newRule)
    if (isEnabled) {
      onChange(newRule)
    }
  }

  const toggleDayOfWeek = (day: number) => {
    const currentDays = rule.days_of_week || []
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort()
    
    // Don't allow empty selection
    if (newDays.length === 0) return
    
    updateRule({ days_of_week: newDays })
  }

  const getRecurrenceSummary = (): string => {
    if (!isEnabled || !rule) return 'Not recurring'
    
    switch (rule.frequency) {
      case 'daily':
        return `Every ${rule.interval && rule.interval > 1 ? rule.interval + ' days' : 'day'}${rule.time ? ' at ' + rule.time : ''}`
      case 'weekdays':
        return `Every weekday${rule.time ? ' at ' + rule.time : ''}`
      case 'weekly':
        const days = (rule.days_of_week || []).map(d => DAYS_OF_WEEK[d].label).join(', ')
        return `Weekly on ${days}${rule.time ? ' at ' + rule.time : ''}`
      case 'biweekly':
        return `Every 2 weeks${rule.time ? ' at ' + rule.time : ''}`
      case 'monthly':
        return `Monthly on day ${rule.day_of_month || 1}${rule.time ? ' at ' + rule.time : ''}`
      case 'custom':
        return 'Custom schedule'
      default:
        return 'Recurring'
    }
  }

  return (
    <div className={`${className}`}>
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between mb-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <button
            type="button"
            onClick={handleEnableToggle}
            disabled={disabled}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              isEnabled ? 'bg-brand' : 'bg-soft'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span 
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-surface shadow transition-transform ${
                isEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <span className="text-sm font-medium text-secondary flex items-center gap-2">
            <Repeat className="w-4 h-4" />
            Recurring
          </span>
        </label>
        {isEnabled && (
          <span className="text-xs text-muted">
            {getRecurrenceSummary()}
          </span>
        )}
      </div>

      {/* Recurrence Options (only show when enabled) */}
      {isEnabled && (
        <div className="space-y-4 bg-surface-alt rounded-lg p-4 border border-soft">
          {/* Frequency Presets */}
          <div>
            <label className="block text-sm font-medium text-secondary mb-2">
              Repeat Pattern
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  onClick={() => updateRule({ frequency: preset.value })}
                  disabled={disabled}
                  className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                    rule.frequency === preset.value
                      ? 'bg-brand-subtle border-brand text-brand'
                      : 'bg-surface border-soft text-secondary hover:border-brand-subtle'
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="font-medium">{preset.label}</div>
                  <div className="text-xs opacity-70">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Day of Week Selection (for weekly/custom) */}
          {(rule.frequency === 'weekly' || rule.frequency === 'custom') && (
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Days of Week
              </label>
              <div className="flex gap-1">
                {DAYS_OF_WEEK.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDayOfWeek(day.value)}
                    disabled={disabled}
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${
                      (rule.days_of_week || []).includes(day.value)
                        ? 'bg-brand text-on-brand'
                        : 'bg-surface-alt text-secondary hover:bg-brand-subtle'
                    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {day.short}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Day of Month (for monthly) */}
          {rule.frequency === 'monthly' && (
            <div>
              <label className="block text-sm font-medium text-secondary mb-2">
                Day of Month
              </label>
              <select
                value={rule.day_of_month || 1}
                onChange={(e) => updateRule({ day_of_month: parseInt(e.target.value) })}
                disabled={disabled}
                className="w-full px-3 py-2 rounded-lg border border-soft bg-surface text-primary"
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <option key={day} value={day}>Day {day}</option>
                ))}
                <option value={-1}>Last day of month</option>
              </select>
            </div>
          )}

          {/* Advanced Options Toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-brand hover:text-brand"
          >
            {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showAdvanced ? 'Hide' : 'Show'} advanced options
          </button>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-4 pt-4 border-t border-soft">
              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Preferred Time
                </label>
                <input
                  type="time"
                  value={rule.time || '09:00'}
                  onChange={(e) => updateRule({ time: e.target.value })}
                  disabled={disabled}
                  className="px-3 py-2 rounded-lg border border-soft bg-surface text-primary"
                />
              </div>

              {/* Interval (for custom) */}
              {rule.frequency === 'custom' && (
                <div>
                  <label className="block text-sm font-medium text-secondary mb-2">
                    Repeat every
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={30}
                      value={rule.interval || 1}
                      onChange={(e) => updateRule({ interval: parseInt(e.target.value) || 1 })}
                      disabled={disabled}
                      className="w-20 px-3 py-2 rounded-lg border border-soft bg-surface text-primary"
                    />
                    <span className="text-secondary">day(s)</span>
                  </div>
                </div>
              )}

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  End Date (optional)
                </label>
                <input
                  type="date"
                  value={rule.end_date || ''}
                  onChange={(e) => updateRule({ end_date: e.target.value || undefined })}
                  disabled={disabled}
                  className="px-3 py-2 rounded-lg border border-soft bg-surface text-primary"
                />
              </div>

              {/* Occurrences (alternative to end date) */}
              <div>
                <label className="block text-sm font-medium text-secondary mb-2">
                  Number of Occurrences (optional)
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={rule.occurrences || ''}
                  onChange={(e) => updateRule({ occurrences: e.target.value ? parseInt(e.target.value) : undefined })}
                  placeholder="Unlimited"
                  disabled={disabled}
                  className="w-32 px-3 py-2 rounded-lg border border-soft bg-surface text-primary"
                />
              </div>
            </div>
          )}

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-brand-subtle rounded-lg text-sm text-brand">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              Recurring items will automatically create scheduled events based on this pattern. 
              You can modify or skip individual occurrences later.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default RecurrencePicker

